import { FC, useState } from 'react';
import {
    Alert,
    Box,
    CircularProgress,
    Grid,
    LinearProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { StatsDateFilter } from '../common/StatsDateFilter';
import { KpiCard } from '../common/KpiCard';
import { StatsBarChart, BarSeries } from '../common/StatsBarChart';
import { useAiOperations } from '../../../hooks/analytics/useAiOperations';

const PROVIDER_COLORS: Record<string, string> = {
    gemini: '#7b1fa2',
    openai: '#1976d2',
    claude: '#43a047',
};

const PROVIDER_LABELS: Record<string, string> = {
    gemini: 'Gemini',
    openai: 'OpenAI',
    claude: 'Claude',
};

const formatLargeNumber = (n: number): string => {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
    return n.toString();
};

const formatDuration = (seconds: number): string => {
    const m = Math.floor(seconds / 60);
    const s = Math.round(seconds % 60);
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const successRateColor = (rate: number): string => {
    if (rate >= 98) return '#16a34a';
    if (rate >= 95) return '#d97706';
    return '#dc2626';
};

type TrendMode = 'calls' | 'tokens' | 'cost';

const STACKED_SERIES: BarSeries[] = [
    { dataKey: 'gemini', name: 'Gemini', color: PROVIDER_COLORS.gemini, stackId: 'provider' },
    { dataKey: 'openai', name: 'OpenAI', color: PROVIDER_COLORS.openai, stackId: 'provider' },
    { dataKey: 'claude', name: 'Claude', color: PROVIDER_COLORS.claude, stackId: 'provider' },
];

const AiOperationsPage: FC = () => {
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [trendMode, setTrendMode] = useState<TrendMode>('calls');

    const handleDateChange = (start: string, end: string) => {
        setStartDate(start);
        setEndDate(end);
    };

    const { data, isLoading, isError, error } = useAiOperations(startDate, endDate);

    const kpi = data?.kpi;
    const callTrend = data?.callTrend ?? [];
    const tokenTrend = data?.tokenTrend ?? [];
    const costTrend = data?.costTrend ?? [];
    const providerDistribution = data?.providerDistribution ?? [];
    const taskTypeBreakdown = data?.taskTypeBreakdown ?? [];
    const errorSummary = data?.errorSummary;
    const errorRateTrend = errorSummary?.errorRateTrend ?? [];
    const topErrors = errorSummary?.topErrors ?? [];
    const batchJobStats = data?.batchJobStats ?? [];

    const trendDataMap: Record<TrendMode, Record<string, unknown>[]> = {
        calls: callTrend as unknown as Record<string, unknown>[],
        tokens: tokenTrend as unknown as Record<string, unknown>[],
        cost: costTrend as unknown as Record<string, unknown>[],
    };

    const trendLabelMap: Record<TrendMode, string> = {
        calls: '호출 수',
        tokens: '토큰',
        cost: '비용',
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>
                AI 운영 통계
            </Typography>

            {/* Date Filter */}
            <Box sx={{ mb: 3 }}>
                <StatsDateFilter onChange={handleDateChange} />
            </Box>

            {isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            )}

            {isError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {(error as Error)?.message ?? '데이터를 불러오지 못했습니다.'}
                </Alert>
            )}

            {data && (
                <>
                    {/* KPI Cards */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                            <KpiCard
                                title="총 API 호출"
                                value={(kpi?.totalCalls ?? 0).toLocaleString()}
                                changePercent={kpi?.callChange}
                                color="blue"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                            <KpiCard
                                title="총 토큰 사용"
                                value={formatLargeNumber(kpi?.totalTokens ?? 0)}
                                changePercent={kpi?.tokenChange}
                                color="purple"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                            <KpiCard
                                title="추정 비용"
                                value={`$${(kpi?.estimatedCost ?? 0).toFixed(2)}`}
                                changePercent={kpi?.costChange}
                                color="orange"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                            <KpiCard
                                title="성공률"
                                value={`${(kpi?.successRate ?? 0).toFixed(1)}%`}
                                changePercent={kpi?.successRateChange}
                                color="green"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                            <KpiCard
                                title="평균 응답시간"
                                value={`${(kpi?.avgResponseTime ?? 0).toFixed(1)}s`}
                                changePercent={kpi?.responseTimeChange}
                                color="red"
                            />
                        </Grid>
                    </Grid>

                    {/* Charts row */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid size={{ xs: 12, md: 8 }}>
                            <Paper sx={{ p: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="subtitle2">일별 API 호출 추이</Typography>
                                    <ToggleButtonGroup
                                        value={trendMode}
                                        exclusive
                                        onChange={(_, val) => val && setTrendMode(val)}
                                        size="small"
                                    >
                                        <ToggleButton value="calls">{trendLabelMap.calls}</ToggleButton>
                                        <ToggleButton value="tokens">{trendLabelMap.tokens}</ToggleButton>
                                        <ToggleButton value="cost">{trendLabelMap.cost}</ToggleButton>
                                    </ToggleButtonGroup>
                                </Box>
                                <StatsBarChart
                                    data={trendDataMap[trendMode]}
                                    series={STACKED_SERIES}
                                    xAxisKey="date"
                                />
                            </Paper>
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <Paper sx={{ p: 2 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                                    Provider별 사용 비율
                                </Typography>
                                {providerDistribution.map((item) => (
                                    <Box key={item.provider} sx={{ mb: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                            <Box
                                                sx={{
                                                    width: 10,
                                                    height: 10,
                                                    borderRadius: '50%',
                                                    bgcolor: PROVIDER_COLORS[item.provider.toLowerCase()] ?? '#888',
                                                    mr: 1,
                                                    flexShrink: 0,
                                                }}
                                            />
                                            <Typography variant="body2" sx={{ fontWeight: 600, flexGrow: 1 }}>
                                                {PROVIDER_LABELS[item.provider.toLowerCase()] ?? item.provider}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {item.ratio.toFixed(1)}%
                                            </Typography>
                                        </Box>
                                        <LinearProgress
                                            variant="determinate"
                                            value={item.ratio}
                                            sx={{
                                                height: 8,
                                                borderRadius: 4,
                                                mb: 0.5,
                                                '& .MuiLinearProgress-bar': {
                                                    bgcolor: PROVIDER_COLORS[item.provider.toLowerCase()] ?? '#888',
                                                },
                                            }}
                                        />
                                        <Typography variant="caption" color="text.secondary">
                                            {item.calls.toLocaleString()}회 / {formatLargeNumber(item.tokens)} 토큰 / ${item.cost.toFixed(2)}
                                        </Typography>
                                    </Box>
                                ))}
                                {providerDistribution.length === 0 && (
                                    <Typography variant="body2" color="text.secondary" align="center">
                                        데이터 없음
                                    </Typography>
                                )}
                            </Paper>
                        </Grid>
                    </Grid>

                    {/* Analysis row */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Paper sx={{ p: 2 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    작업 유형별 호출 분포
                                </Typography>
                                <TableContainer sx={{ maxHeight: 360 }}>
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>작업 유형</TableCell>
                                                <TableCell align="right">호출</TableCell>
                                                <TableCell align="right">토큰</TableCell>
                                                <TableCell align="right">성공률</TableCell>
                                                <TableCell align="right">평균 응답</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {taskTypeBreakdown.map((row) => (
                                                <TableRow key={row.taskType}>
                                                    <TableCell>{row.taskType}</TableCell>
                                                    <TableCell align="right">{row.calls.toLocaleString()}</TableCell>
                                                    <TableCell align="right">{formatLargeNumber(row.tokens)}</TableCell>
                                                    <TableCell
                                                        align="right"
                                                        sx={{ color: successRateColor(row.successRate), fontWeight: 600 }}
                                                    >
                                                        {row.successRate.toFixed(1)}%
                                                    </TableCell>
                                                    <TableCell align="right">{row.avgResponseTime.toFixed(1)}s</TableCell>
                                                </TableRow>
                                            ))}
                                            {taskTypeBreakdown.length === 0 && (
                                                <TableRow>
                                                    <TableCell colSpan={5} align="center">
                                                        데이터 없음
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Paper sx={{ p: 2 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    에러 요약
                                </Typography>
                                <Box sx={{ mb: 2 }}>
                                    <StatsBarChart
                                        data={errorRateTrend.map((item) => ({
                                            date: item.date,
                                            errorRate: item.errorRate,
                                        })) as unknown as Record<string, unknown>[]}
                                        series={[
                                            { dataKey: 'errorRate', name: '에러율 (%)', color: '#ef4444' },
                                        ]}
                                        xAxisKey="date"
                                        height={100}
                                    />
                                </Box>
                                {topErrors.map((err, idx) => (
                                    <Box
                                        key={`${err.errorType}-${idx}`}
                                        sx={{
                                            p: 1.5,
                                            mb: 1,
                                            borderRadius: 1,
                                            bgcolor: '#fff7ed',
                                            border: '1px solid #fed7aa',
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#9a3412' }}>
                                                {err.errorType}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: '#c2410c' }}>
                                                {err.count.toLocaleString()}회 ({err.ratio.toFixed(1)}%)
                                            </Typography>
                                        </Box>
                                    </Box>
                                ))}
                                {topErrors.length === 0 && (
                                    <Typography variant="body2" color="text.secondary" align="center">
                                        에러 없음
                                    </Typography>
                                )}
                            </Paper>
                        </Grid>
                    </Grid>

                    {/* Batch table */}
                    <Alert severity="info" sx={{ mb: 2 }}>
                        비용은 토큰 단가 기반 추정치이며 실제 청구 금액과 다를 수 있습니다.
                    </Alert>
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            배치 작업 현황
                        </Typography>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>작업 유형</TableCell>
                                        <TableCell align="right">총 건수</TableCell>
                                        <TableCell align="right">성공</TableCell>
                                        <TableCell align="right">실패</TableCell>
                                        <TableCell align="right">성공률</TableCell>
                                        <TableCell align="right">평균 처리시간</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {batchJobStats.map((row) => (
                                        <TableRow key={row.jobType}>
                                            <TableCell>{row.jobType}</TableCell>
                                            <TableCell align="right">{row.total.toLocaleString()}</TableCell>
                                            <TableCell align="right">{row.success.toLocaleString()}</TableCell>
                                            <TableCell align="right">{row.failed.toLocaleString()}</TableCell>
                                            <TableCell
                                                align="right"
                                                sx={{ color: successRateColor(row.successRate), fontWeight: 600 }}
                                            >
                                                {row.successRate.toFixed(1)}%
                                            </TableCell>
                                            <TableCell align="right">{formatDuration(row.avgDurationSeconds)}</TableCell>
                                        </TableRow>
                                    ))}
                                    {batchJobStats.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center">
                                                데이터 없음
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </>
            )}
        </Box>
    );
};

export default AiOperationsPage;
